/**
 * The English catalogue — and the source of truth for every message key.
 *
 * `cs` and `de` are typed against this object, so adding a key here without
 * translating it is a type error rather than a surprise at runtime. Keys are
 * flat and dotted: a nested structure reads nicely but makes a key impossible
 * to grep for, which is what you actually do when a string looks wrong.
 *
 * Plural forms are objects. Only the categories a language distinguishes need
 * to be present; `Intl.PluralRules` picks between them.
 */
export const en = {
  // --- Navigation ----------------------------------------------------------
  'nav.label': 'Main navigation',
  'nav.today': 'Today',
  'nav.upcoming': 'Upcoming',
  'nav.backlog': 'Backlog',
  'nav.assignedToMe': 'Assigned to me',
  'nav.assignedByMe': 'Assigned by me',
  'nav.completed': 'Completed',
  'nav.labels': 'Labels',
  'nav.badgeBreakdown': '{overdue} overdue, {today} for today',
  'nav.settings': 'Settings',
  'nav.signOut': 'Sign out',

  // --- Shared --------------------------------------------------------------
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.saved': 'Saved.',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.closeDialog': 'Close dialog',
  'common.delete': 'Delete',
  'common.add': 'Add',
  'common.continue': 'Continue',
  'common.retry': 'Try again',
  'common.loading': 'Loading…',
  'common.search': 'Search…',
  'common.searching': 'Searching…',
  'common.uploading': 'Uploading…',
  'common.undo': 'Undo',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.pagination': 'Pagination',
  'common.me': 'Me',
  'common.system': 'System',
  'common.all': 'All',
  'common.none': 'None',
  'common.enterSaves': 'Enter to save',
  'common.range': '{from}–{to} of {total}',
  'common.error': 'Something went wrong. Please try again.',

  // --- Today ---------------------------------------------------------------
  'today.empty.title': 'Nothing for today.',
  'today.empty.hint': 'Add a task below, or pull one out of the backlog.',
  'today.completedWord': 'completed',
  'today.overdue': 'Overdue',
  'today.progress': {
    one: '{done} of {count} done',
    other: '{done} of {count} done',
  },

  // --- Priority bands ------------------------------------------------------
  'band.mustDo': 'Must do',
  'band.normal': 'Normal',
  'band.ifThereIsTime': 'If there is time',

  // --- Priorities ----------------------------------------------------------
  'priority.none': 'None',
  'priority.low': 'Low',
  'priority.normal': 'Normal',
  'priority.high': 'High',
  'priority.urgent': 'Urgent',
  'priority.label': 'Priority',

  // --- Backlog and delegation ---------------------------------------------
  'backlog.empty.title': 'The backlog is empty.',
  'backlog.empty.hint': 'Everything you want to do some day, but not today.',
  'assignedToMe.empty.title': 'Nobody has delegated anything to you.',
  'assignedToMe.empty.hint': 'Tasks someone else created and assigned to you.',
  'assignedByMe.empty.title': 'You have not delegated anything.',
  'assignedByMe.empty.hint':
    'Type @ in a task title and pick the person who should deal with it.',

  // --- Upcoming ------------------------------------------------------------
  'upcoming.empty.title': 'Nothing scheduled.',
  'upcoming.empty.hint': 'Tasks with a future date appear here, grouped by day.',

  // --- Completed -----------------------------------------------------------
  'completed.empty.title': 'Nothing completed yet.',
  'completed.empty.hint':
    'Completed tasks are never deleted — you can reopen any of them.',
  'completed.sortBy': 'Sort by',
  'completed.search': 'Search history…',
  'completed.search.hint': 'Try other words. A space means another word, not a phrase.',
  'completed.searchingAll': 'Searching the whole history.',
  'completed.recentOnly': 'The last {days} days. Older work is found by searching.',
  'completed.sort.completedAt': 'Completion date',
  'completed.sort.priority': 'Priority',
  'completed.sort.title': 'Title',
  'completed.groupBy': 'Group by',
  'completed.group.none': 'No grouping',
  'completed.group.day': 'Day',
  'completed.group.label': 'Label',
  'completed.group.priority': 'Priority',
  'completed.noLabel': 'No label',
  'completed.reopen': 'Reopen',

  // --- Labels --------------------------------------------------------------
  'label.singular': 'Label',
  'label.plural': 'Labels',
  'label.add': 'Add label',
  'label.new': 'New label…',
  'label.create': 'Create label',
  'label.rename': 'Rename',
  'label.color': 'Colour',
  'label.empty.title': 'Nothing filed here yet.',
  'label.empty.hint': 'Add a label from a task’s detail.',

  // --- Tasks ---------------------------------------------------------------
  'complete.return': 'Return',
  'complete.confirm': 'Finish',
  'task.detail': 'Task detail',
  'task.title': 'Task title',
  'task.newTitle': 'New task title',
  'task.complete': 'Mark as done',
  'task.reopen': 'Reopen',
  'task.delete': 'Delete task',
  'task.deleted': 'Task deleted.',
  'task.completedToast': '“{title}” done.',
  'task.reopenedToast': 'Task reopened.',
  'task.createdBy': 'Created by',
  'task.assignee': 'Assignee',
  'task.delegateTo': 'Delegate to',
  'task.mentionPlaceholder': '@username',
  'task.scheduled': 'Scheduled',
  'task.createdAt': 'Created',
  'task.completedAt': 'Completed',
  'task.blockedBySubtasks': 'Finish the open subtasks first.',
  'task.protected': 'Guarded',
  'task.protected.hint': 'A project — open it to release it for closing.',
  'task.protected.toggle': 'Guard against closing by accident',
  'task.arm': 'Ready to close',
  'task.armed': 'Released — the tick will work now',
  'task.openSubtasks': {
    one: '{count} open subtask',
    other: '{count} open subtasks',
  },
  'task.subtaskProgress': '{done}/{total}',

  // --- Quick add -----------------------------------------------------------
  'quickAdd.title': 'Quick Add',
  'quickAdd.open': '+ Add task',
  'quickAdd.prompt': 'What needs doing?',
  'quickAdd.placeholder': 'Task title…  @ to delegate',
  'quickAdd.pasteHint': '⌘V pastes a screenshot · Enter saves',
  'quickAdd.tomorrow': 'Tomorrow',
  'quickAdd.attachmentFailed':
    'The task was created, but the attachment could not be uploaded.',

  // --- Subtasks ------------------------------------------------------------
  'subtask.plural': 'Subtasks',
  'subtask.add': 'Add subtask',
  'subtask.placeholder': 'What needs doing?',
  'subtask.title': 'Subtask title',
  'subtask.delete': 'Delete subtask: {title}',
  'subtask.remaining': {
    one: '{count} of {total} subtasks is not done yet',
    other: '{count} of {total} subtasks are not done yet',
  },
  'label.colorNamed': 'Colour {color}',
  'subtask.open': 'Open this step as a task',
  'subtask.schedule': 'Give “{title}” a date',
  'task.openParent': 'Open the parent task',
  'task.projectInherited': '{name} — from the parent task',
  'sort.label': 'Sort',
  'sort.default': 'Default',
  'sort.postponed': 'Postponed',
  'sort.priority': 'Priority',
  'sort.project': 'Project',
  'sort.parent': 'Parent task',
  'projects.unfiled': 'No project',
  'projects.unfiledHint': 'Tasks that belong nowhere — together, so the totals add up',
  'nav.teams': 'Teams',
  'teams.title': 'Teams',
  'teams.subtitle': 'You can hand work only to someone you share a team with. Accounts exist by invitation only.',
  'teams.namePlaceholder': 'Team name',
  'teams.add': 'Create team',
  'teams.empty.title': 'No teams yet',
  'teams.empty.hint': 'A team decides who you can delegate to, and who you can even see in the picker.',
  'teams.managedBy': 'Manager: {name}',
  'teams.noManager': 'No manager',
  'teams.manager': 'manager',
  'teams.makeManager': 'Make manager',
  'teams.makeManagerHint': 'A team has exactly one manager — the current one becomes a member.',
  'teams.removeMember': 'Remove from team',
  'teams.noMembers': 'Nobody in this team yet.',
  'teams.delete': 'Delete team',
  'teams.deleteConfirm': 'Delete team {name}? Memberships go; tasks stay where they are.',
  'invite.email': 'Email',
  'invite.validity': 'Valid for (days)',
  'invite.send': 'Send invitation',
  'invite.asManager': 'Invite straight as the team\'s manager',
  'invite.revoke': 'Withdraw',
  'invite.expires': 'until {date}',
  'invite.emailed': 'The invitation was emailed. The link is here too, in case it does not arrive:',
  'invite.notEmailed': 'The email could not be sent — deliver the link yourself:',
  'invite.shownOnce': 'Shown only now. It cannot be recovered — issue a new invitation instead.',
  'invite.deadTitle': 'This invitation is no longer valid',
  'invite.deadHint': 'It expired, was withdrawn, or has already been used. Ask for a new one.',
  'invite.welcome': 'Welcome to MirOnDo',
  'invite.welcomeTeam': 'An invitation to {team}',
  'invite.invitedBy': '{name} invited you.',
  'invite.invitedByUnknown': 'Somebody invited you to MirOnDo.',
  'invite.asManagerNote': 'You are to become the team\'s manager.',
  'invite.signInWith': 'Please sign in with {email} — the invitation is bound to that address.',
  'teams.memberCount': {
    one: '{count} member',
    other: '{count} members',
  },
  'waiting.title': 'Waiting on others',
  'waiting.today': 'today',
  'waiting.seeAll': 'See everything I handed over →',
  'waiting.days': {
    one: '{count} day',
    other: '{count} days',
  },
  'waiting.stale': {
    one: '{count} over a week',
    other: '{count} over a week',
  },
  'teams.empty.notAdmin': 'You are not in a team yet. A team manager or the system administrator has to invite you.',
  'invite.accept': 'Accept invitation',
  'invite.joined': 'You are in {team}.',
  'invite.joinedNoTeam': 'Invitation accepted.',
  'invite.backToApp': 'Back to the app',
  'invite.wrongAccount': 'This invitation is for {invited}, but you are signed in as {current}. Please sign in with that address.',
  'teams.addExisting': 'Add somebody who already has an account',
  'teams.addExistingPlaceholder': 'Search a name or @handle…',
  'teams.addToTeam': 'Add to team',
  'handover.title': 'Hand over',
  'handover.what': 'What I need from them',
  'handover.kind.DO': 'Do it',
  'handover.kind.REVIEW': 'Review',
  'handover.kind.APPROVE': 'Approve',
  'handover.kind.QUESTION': 'Answer',
  'handover.hint.DO': 'They do the work — the task ends with them.',
  'handover.hint.REVIEW': 'They look it over and hand it back.',
  'handover.hint.APPROVE': 'They decide, and hand it back with the answer.',
  'handover.hint.QUESTION': 'I need an answer; the work stays mine.',
  'handover.who': 'To whom',
  'handover.whoPlaceholder': 'Search a teammate…',
  'handover.change': 'change',
  'handover.note': 'What, where, when, how',
  'handover.notePlaceholder': 'Say exactly what you need — where to find it, what to watch for, by when. It is posted as a comment too, so they can reply to it.',
  'handover.by': 'Back by (optional)',
  'handover.send': 'Hand over',
  'handover.askedBy': '{name} is asking',
  'handover.dueBy': 'by {date}',
  'handover.answer': 'Answer and return',
  'handover.answerPlaceholder': 'What you did, or how it turned out…',
  'handover.handBack': 'Hand back',
  'handover.returned': 'Returned — {name} has it now.',
  'handover.handedTo': 'Handed over — {name} has it now.',
  'nav.involved': 'I took part',
  'involved.empty.title': 'Nothing has moved on yet',
  'involved.empty.hint': 'Once you hand a task over or say something on one, you will find it here even after it leaves your own lists.',
  'task.readOnly': 'Read-only — somebody else holds this now',
  'nav.stats': 'Statistics',
  'stats.title': 'Statistics',
  'stats.subtitle': 'How much, how long and who. Never what — a team\'s numbers are its manager\'s to see.',
  'stats.scope.me': 'Me',
  'stats.range.30': '30 days',
  'stats.range.90': '90 days',
  'stats.range.365': 'Year',
  'stats.kpi.completed': 'Completed',
  'stats.kpi.open': 'Open',
  'stats.kpi.overdue': 'Overdue',
  'stats.kpi.tracked': 'Tracked',
  'stats.kpi.median': 'Median to complete',
  'stats.kpi.medianNote': 'from creation, in days',
  'stats.kpi.created': 'Newly created',
  'stats.trend.title': 'Tasks completed per week',
  'stats.people.title': 'Who holds what, who finished what',
  'stats.people.open': 'open',
  'stats.people.completed': 'completed',
  'stats.hours.title': 'Hours tracked',
  'stats.projects.title': 'Where the work goes',
  'stats.projects.project': 'Project',
  'stats.projects.open': 'Open',
  'stats.projects.completed': 'Completed',
  'stats.projects.hours': 'Hours',
  'stats.projects.none': 'No project',
  'stats.matrix.title': 'Who worked on what',
  'stats.handover.title': 'Handovers',
  'stats.handover.sent': 'handed over',
  'stats.handover.returned': 'returned',
  'stats.handover.open': 'awaiting an answer',
  'stats.handover.turnaround': 'median turnaround',
  'stats.days': '{value} d',
  'stats.hours': '{value} h',
  'stats.person.median': 'median {value} d',
  'stats.person.p90': 'p90 {value} d',
  'stats.empty': 'Nothing happened in this period.',
  'stats.table': 'Table',
  'stats.chart': 'Chart',
  'common.rename': 'Rename',
  'kb.renameNote': 'Rename note',
  'kb.renameTopic': 'Rename topic',
  'kb.template': 'Access template',
  'kb.templatePick': 'Pick a template (FTP, database, SSH…)',
  'kb.templateMine': 'My templates',
  'kb.templateManage': 'Manage templates…',
  'kb.templateNone': 'None of your own yet. Fill in the fields below and save them as a template.',
  'kb.templateRename': 'Rename template',
  'kb.templateSave': 'Save as template',
  'kb.templateNamePlaceholder': 'Template name for fields: {fields}',
  'editor.todo': 'To do (⌘↩ cycles: to do → done → none)',
  'editor.todoShort': 'To do',
  'editor.hintTouch': 'Task: the To do button or type /todo · done: tap the box',
  'editor.indent': 'Indent (Tab)',
  'editor.outdent': 'Outdent (⇧Tab)',
  'editor.help.todo': 'A task to tick off',
  'editor.help.cycle': 'Cycle to do → done → none',
  'editor.help.indent': 'Indent / outdent',
  'editor.help.slash': 'Commands: todo, done, h1, date, bullet…',
  'editor.help.save': 'Save',
  'task.stepOf': 'Step of {title}',
  'task.makeStep': 'Make it a step of…',
  'task.makeStep.search': 'Find the parent task…',
  'task.promote': 'Promote',
  'common.noResults': 'Nothing found',
  'subtask.allDone': 'All subtasks done',

  // --- Description and attachments ----------------------------------------
  'editor.bold': 'Bold',
  'editor.italic': 'Italic',
  'editor.code': 'Code',
  'editor.bulletList': 'Bulleted list',
  'editor.numberedList': 'Numbered list',
  'editor.quote': 'Quote',
  'editor.link': 'Link',
  'editor.insertImage': 'Insert an image — or paste one with ⌘V, or drop it here',
  'editor.preview': 'Preview',
  'editor.edit': 'Edit',
  'editor.help': 'Markdown',
  'editor.help.heading': 'Heading',
  'editor.help.image': 'Image',
  'editor.help.divider': 'Divider',
  'editor.help.autocomplete':
    'Start typing #, -, > or [ and the editor suggests the rest. Enter continues a list.',

  'description.label': 'Description',
  'checklist.start': 'Checklist',
  'checklist.addPlaceholder': 'Add an item…',
  'checklist.uncheckAll': 'Uncheck all',
  'description.add': 'Add a description…  (Markdown supported)',
  'description.placeholder': 'Description…  /todo task, **bold**, - list',
  'attachment.plural': 'Attachments',
  'attachment.count': {
    one: '{count} attached',
    other: '{count} attached',
  },
  'attachment.download': 'Download',
  'attachment.open': 'Open {name}',
  'attachment.downloadNamed': 'Download {name}',
  'attachment.deleteNamed': 'Delete {name}',
  'attachment.unsupportedType': '{name}: only PNG, JPEG, WEBP and PDF are supported.',
  'attachment.remove': 'Remove {name}',
  'attachment.add': 'Attach a file',
  'attachment.closePreview': 'Close preview',
  'attachment.uploadFailed': 'The attachment could not be uploaded.',
  'attachment.deleteFailed': 'The attachment could not be deleted.',

  // --- Comments ------------------------------------------------------------
  'activity.handedOver': 'Handed to @{to} — {kind}',
  'activity.handedBack': 'Handed back to @{to}',
  'activity.title': 'History',
  'activity.created': 'Task created',
  'activity.assigned': 'Assigned to @{to}',
  'activity.reassigned': 'Reassigned from @{from} to @{to}',
  'activity.scheduled': 'Scheduled for {to}',
  'activity.rescheduled': 'Moved from {from} to {to}',
  'activity.movedToBacklog': 'Moved to the backlog',
  'activity.priorityChanged': 'Priority changed to {to}',
  'activity.statusChanged': 'Status changed to {to}',
  'activity.completed': 'Completed',
  'activity.reopened': 'Reopened',
  'activity.cancelled': 'Cancelled',
  'activity.attachmentAdded': 'Attachment added {name}',
  'activity.attachmentRemoved': 'Attachment removed {name}',
  'activity.commentAdded': 'Comment added',
  'activity.labelsSet': 'Labels set: {labels}',
  'activity.labelsCleared': 'All labels removed',
  'activity.subtaskAdded': 'Subtask added “{title}”',
  'activity.subtaskRemoved': 'Subtask removed “{title}”',
  'activity.updated': 'Task edited',
  'activity.deleted': 'Task deleted',
  'activity.restored': 'Task restored',

  'comment.plural': 'Comments',
  'today.moveAll': 'Move all:',
  'task.move': 'Move',
  'task.actionsFor': 'Actions for {title}',
  'comment.count': {
    one: '{count} comment',
    other: '{count} comments',
  },
  'comment.placeholder': 'Write a comment… (⌘Enter sends)',
  'comment.send': 'Send comment',

  // --- Command palette -----------------------------------------------------
  'command.placeholder': 'Search tasks or run a command…',
  'command.label': 'Search tasks or run a command',
  'command.results': 'Results',
  'command.actions': 'Actions',
  'command.newTask': 'New task',
  'command.goToday': 'Go to Today',
  'command.goUpcoming': 'Go to Upcoming',
  'command.goBacklog': 'Go to Backlog',
  'command.goCompleted': 'Go to Completed',
  'command.noResults': 'Nothing found.',
  'mention.noMatch': 'Nobody matches “@{query}”.',
  'command.users': 'People',

  // --- Move actions --------------------------------------------------------
  'move.today': 'Today',
  'move.tomorrow': 'Tomorrow',
  'move.backlog': 'Backlog',
  'move.pickDate': 'Pick a date',

  // --- Dates ---------------------------------------------------------------
  'date.today': 'Today',
  'date.tomorrow': 'Tomorrow',
  'date.yesterday': 'Yesterday',
  'date.lastWeek': 'last week',
  'date.overdueBy': {
    one: '{count} day late',
    other: '{count} days late',
  },

  // --- Sign-in and onboarding ---------------------------------------------
  'login.tagline': 'Tasks that don’t disappear.',
  'login.motto': 'Turn on. Get it done.',
  'login.promise':
    'An unfinished task never hides itself. It stays in view until you deal with it, or move it on purpose.',
  'login.privacy':
    'Sign-in uses your Google account. MirOnDo stores only your email, name and profile picture.',
  'login.language': 'Language',
  'login.google': 'Sign in with Google',
  'login.cancelled': 'Sign-in was cancelled.',
  'login.failed': 'Sign-in failed. Please try again.',
  'onboarding.title': 'Pick a username',
  'onboarding.hint':
    'This is how other people find you when they want to delegate something. They will type',
  'onboarding.example': 'name',
  'onboarding.or': 'Or:',
  'onboarding.changeable': 'You can change it later in settings.',
  'onboarding.username': 'Username',
  'onboarding.rules':
    'At least two characters: lowercase letters, digits, “_” and “-”.',
  'onboarding.available': 'Available.',
  'onboarding.taken': 'That username is taken.',

  // --- Settings ------------------------------------------------------------
  'kb.title': 'Knowledge',
  'kb.topicHint': 'Guides, references, credentials — one shelf per topic.',
  'kb.newNote': 'New note… ↩ creates it',
  'kb.empty.title': 'Nothing here yet.',
  'kb.empty.hint': 'Type a title above. [[Wikilinks]] connect notes to each other.',
  'kb.deleteTopicConfirm': 'Delete this topic with all its notes and secrets?',
  'kb.deleteSecretConfirm': 'Delete this secret permanently?',
  'kb.secrets': 'Secrets',
  'kb.secretsHint':
    'Encrypted on the server. Every reveal is recorded. Losing the server key loses the secrets — the notes never.',
  'kb.newSecret': 'New secret',
  'kb.secretName': 'Name — e.g. FTP customer X',
  'kb.fieldName': 'field',
  'kb.fieldValue': 'value',
  'kb.addField': 'add a field',
  'kb.reveal': 'Reveal',
  'kb.hide': 'Hide',
  'kb.backlinks': 'Linked from',
  'kb.phantomHint': 'This note does not exist yet — click to create it.',
  'kb.sensitive': 'Sensitive',
  'kb.deleteNoteConfirm': 'Delete this note?',
  'kb.maskedBody': '*The content is hidden — use Reveal above.*',
  'kb.lastEdit': 'Last edited by {who}',
  'kb.bodyPlaceholder': 'Write… /todo makes a task, [[link]] connects notes',
  'kb.editorHint': '⌘↩ or /todo makes a task, ⌘↩ again marks it done · Enter new item · Tab indents · [[ notes · ⌘S saves · esc discards',
  'kb.share': 'Share the topic',
  'kb.shareDisclosure':
    'Grants live access to {notes} notes, {sensitive} of them sensitive. Revoke any time.',
  'kb.roleViewer': 'Viewer',
  'kb.roleEditor': 'Editor',
  'kb.revoke': 'Revoke access for {who}',
  'kb.sharedBy': 'Shared by {who} — you are a {role}.',
  'kb.leave': 'Leave',
  'kb.searchPeople': 'Search people…',
  'kb.searchNotes': 'Search notes…',
  'kb.pickTopic': 'Pick a topic on the left, or create one.',
  'kb.notesOnTask': 'Notes',
  'kb.newTopic': 'New topic…',

  'settings.title': 'Settings',
  'settings.account': 'Account',
  'settings.name': 'Name',
  'settings.username': 'Username',
  'settings.email': 'Email',
  'settings.language': 'Language',
  'settings.language.hint':
    'Applies to the web app and the macOS app — the preference is stored on your account.',
  'settings.timezone': 'Time zone',
  'settings.timezone.hint':
    'Decides what “today” means — and therefore which tasks are overdue.',
  'settings.theme': 'Theme',
  'settings.theme.system': 'Match system',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.reminders': 'Reminders',
  'settings.reminders.hint':
    'Used by the macOS app. The setting is stored on the server, so it applies on every device.',
  'settings.reminders.enabled': 'Remind me',
  'settings.reminders.interval': 'Every',
  'settings.onlyBetween': 'Only between',
  'settings.and': 'and',
  'settings.reminders.start': 'Reminders from',
  'settings.reminders.end': 'Reminders until',
  'settings.defaultDestination': 'New tasks go to',
  'settings.defaultDestination.hint':
    'Where a quick-added task lands when you do not choose a date.',
  'settings.weekStart': 'Week starts on',
  'calendar.today': 'Today’s calendar',
  'calendar.booked': '· {hours} booked',
  'calendar.allDay': 'all day',
  'calendar.problem.API_NOT_ENABLED':
    'The Google Calendar API is not enabled for this project. Enable it in the Google Cloud console, then reload.',
  'calendar.problem.SCOPE_MISSING':
    'The connection is missing calendar permission. Disconnect and connect again, and accept the calendar access.',
  'calendar.problem.ACCESS_REVOKED':
    'Access was withdrawn in your Google account. Connect again.',
  'calendar.problem.REFRESH_REJECTED':
    'Google refused to renew the connection. Disconnect and connect again.',
  'calendar.problem.VAULT_KEY_CHANGED':
    'The stored connection can no longer be read — the vault key changed. Disconnect and connect again.',
  'calendar.problem.CALENDAR_NOT_FOUND':
    'This account has no primary calendar.',
  'calendar.problem.GOOGLE_UNREACHABLE':
    'Google could not be reached. Usually temporary.',
  'calendar.problem.FETCH_FAILED':
    'The calendar could not be read.',
  'calendar.feeds.title':
    'Other calendars (ICS)',
  'calendar.feeds.hint':
    'Outlook, iCloud and most company calendars can publish a private ICS address — paste it here and its meetings join your day. Read-only, like everything here.',
  'calendar.feeds.placeholder':
    'https://…/calendar.ics',
  'calendar.feeds.add':
    'Subscribe',
  'calendar.feeds.checking':
    'Checking…',
  'calendar.feeds.remove':
    'Remove',
  'calendar.feeds.addFailed':
    'That address did not answer with a calendar.',
  'calendar.problem.FEED_UNREACHABLE':
    'The calendar address could not be reached.',
  'calendar.problem.FEED_REDIRECTED':
    'The address answers with a redirect — paste the final address instead.',
  'calendar.title': 'Google Calendar',
  'calendar.hint':
    'Today’s meetings appear above your tasks, so you can see how much of the day is already taken. MirOnDo only ever reads — it never writes to your calendar.',
  'calendar.connect': 'Connect a calendar',
  'calendar.connected': 'Connected: {email}',
  'calendar.disconnect': 'Disconnect',
  'calendar.failed': 'The connection failed. Try again.',
  'telegram.hint':
    'The link is yours alone — each account connects its own chat. A pat on the back lands there when you finish a task, and forwarding any message to the bot turns it into your task.',
  'telegram.connect': 'Connect {bot}',
  'telegram.linked': 'Connected to {bot}',
  'telegram.unlink': 'Disconnect',
  'tgCapture.title': 'Telegram — mention capture',
  'tgCapture.hint':
    'Connects your own Telegram account (not the bot). When someone @mentions you in a group, a task is created. Your login session is stored encrypted on this server; disconnect logs it out at Telegram too.',
  'tgCapture.connect': 'Send code',
  'tgCapture.codeSent': 'Telegram sent a code to {phone}. Enter it here.',
  'tgCapture.codeSent.app':
    'The code went **to the Telegram app** on {phone} — look for a message from the "Telegram" account, not for a text.',
  'tgCapture.codeSent.sms': 'Telegram texted a code to {phone}. Enter it here.',
  'tgCapture.codeSent.call': 'Telegram will call {phone} and read the code out.',
  'tgCapture.code': 'Code',
  'tgCapture.resend': 'Send the code again',
  'tgCapture.changeNumber': 'Use a different number',
  'tgCapture.codeResent': 'A new code is on its way.',
  'tgCapture.password': 'Two-step password',
  'tgCapture.passwordHint':
    'This account has two-step verification — its password is needed once.',
  'tgCapture.connected': 'Watching mentions for {phone}',
  'settings.shortcuts': 'Keyboard shortcuts',
  'settings.shortcut.newTask': 'New task',
  'settings.shortcut.search': 'Search and commands',
  'settings.shortcut.today': 'Go to Today',
  'settings.minutes': {
    one: '{count} minute',
    other: '{count} minutes',
  },

  // --- Errors --------------------------------------------------------------
  'common.andMore': '+{count} more',
  'label.yours': 'Your labels',
  'label.noneYet': 'None yet. Create the first one below.',
  'task.reopenNamed': 'Reopen: {title}',
  'task.completeNamed': 'Complete: {title}',
  'mention.typeMore': 'Type at least two characters…',
  'attachment.uploadFailedNamed': '{name}: upload failed.',
  'attachment.uploadingCount': 'Uploading {count}…',
  'attachment.dropHint': ', drop one here, or paste a screenshot with',
  'description.pasteHint': 'pastes a screenshot straight into the text',
  'description.saveHint': 'saves',
  'description.discardHint': 'discards',

  'error.taskCreate': 'The task could not be created.',
  'error.taskUpdate': 'The change could not be saved.',
  'error.taskComplete': 'The task could not be completed.',
  'error.taskReopen': 'The task could not be reopened.',
  'error.taskDelete': 'The task could not be deleted.',
  'error.taskMove': 'The task could not be moved.',
  'error.taskDelegate': 'The task could not be delegated.',
  'error.priorityChange': 'The priority could not be changed.',
  'error.subtaskCreate': 'The subtask could not be added.',
  'error.subtaskUpdate': 'The subtask could not be changed.',
  'error.subtaskDelete': 'The subtask could not be deleted.',
  'error.labelCreate': 'The label could not be created.',
  'error.labelUpdate': 'The label could not be changed.',
  'error.labelDelete': 'The label could not be deleted.',
  'error.labelAssign': 'The labels could not be saved.',
  'error.attachmentUpload': 'The attachment could not be uploaded.',
  'error.attachmentDelete': 'The attachment could not be deleted.',
  'error.blockedBySubtasks': 'Finish the open subtasks before closing this task.',
  'error.usernameTaken': 'That username is taken.',
  'error.notFound': 'That is not here — it may have been deleted.',
  'error.forbidden': 'You do not have access to that.',
  'error.offline': 'No connection to the server.',
  'error.tgFloodWait': 'Telegram is rate-limiting sign-in. Try again in {seconds}s.',
  'nav.guide':
    'Guide',
  'telegram.guideLink':
    'How capturing from Telegram works',
  'task.postponed.count': {
    one: 'Postponed {count} time',
    other: 'Postponed {count} times',
  },
  'guide.title':
    'Guide: capturing from Telegram',
  'guide.intro':
    'The fastest way to get a task into MirOnDo is to type it to the bot on Telegram — without opening the app. Here is everything the bot understands.',
  'guide.connect.title':
    'Linking your account',
  'guide.connect.body':
    'In Settings → Telegram, tap Connect. A chat with {bot} opens, and its Start button pairs it with your account. The bot is yours alone: what you write to it, only you see.',
  'guide.typing.title':
    'A new task = one message',
  'guide.typing.lead':
    'Type a message to the bot and it becomes a task. You can add a day and a priority at the end:',
  'guide.typing.ex1':
    'Call the bank tomorrow !!',
  'guide.typing.ex1.means':
    'urgent, tomorrow',
  'guide.typing.ex2':
    'Send the invoice 2026-09-03 !',
  'guide.typing.ex2.means':
    'high priority, 3 September',
  'guide.typing.ex3':
    'File the complaint in 5 days',
  'guide.typing.ex3.means':
    'normal, in 5 days',
  'guide.typing.understands':
    'The bot understands: today, tomorrow, weekday names (on Friday = the next Friday), in N days, in N weeks, +5d, and dates like 2026-09-03. One ! is high priority, two !! is urgent.',
  'guide.typing.untouched':
    'Anything the bot does not recognise stays in the title. It never deletes a word just because it looks like shorthand.',
  'guide.list.title':
    'Shopping lists and checklists',
  'guide.list.lead':
    'A colon after the title says “what follows is a list”. Every further line is then an item — no bullets needed:',
  'guide.list.example':
    'Groceries:\nmilk\nbread\nbutter',
  'guide.list.reply':
    'Captured in MirOnDo: “Groceries” · 3 items',
  'guide.list.alt':
    'Three asterisks after the title work instead of the colon — louder, and impossible to miss:',
  'guide.list.altExample':
    'Groceries ***\nmilk\nbread',
  'guide.list.boxes':
    'In the task, the items become tick-off boxes — you tick what is bought, nothing gets deleted. Lines starting with a dash (- budget) convert even without the colon, and a plain sentence under the title stays a sentence: a note never wakes up as a checklist.',
  'guide.list.combine':
    'It composes with a day and a priority:',
  'guide.list.combineExample':
    'Groceries: !! tomorrow\nmilk\nbread',
  'guide.subtask.title':
    'A step of a big project',
  'guide.subtask.lead':
    'File a step under a long-term task by ending with >> and a bit of the project name — one word is enough, diacritics optional:',
  'guide.subtask.example':
    'Order windows !! tomorrow >> cabin',
  'guide.subtask.example.means':
    'an urgent step under “Cabin renovation”',
  'guide.subtask.buttons':
    'Cannot recall the name at all? Send just >> with nothing after it — the bot offers your projects as buttons and you tap one. It offers locked tasks (protected from quick closing) and tasks that already have steps.',
  'guide.subtask.buttonsExample':
    'Buy paint >>',
  'guide.subtask.clients':
    'A client name works too — “Banners >> xiaomi” creates the task already billed to that client. A bare >> offers projects and clients alike.',
  'guide.subtask.safe':
    'When nothing matches, the task is saved on its own and the bot says so — a message is never lost.',
  'guide.forward.title':
    'Forwarded messages',
  'guide.forward.body':
    'Forward the bot any message and it becomes a task. The description records who wrote it, when, and a link back to the conversation on Telegram — the context stays within reach.',
  'guide.forward.untouched':
    'The bot never looks for shorthand or lists in forwarded text. Those are someone else’s words — a “tomorrow” in the middle of a colleague’s sentence must not move your task.',
  'guide.mentions.title':
    'Mentions in groups',
  'guide.mentions.body':
    'In Settings → Telegram you can additionally connect your own Telegram account. When someone mentions you (@your_name) in any group, the mention is saved as a task by itself — with a link to the original message, and who wrote it where.',
  'guide.praise.title':
    'Finished tasks',
  'guide.praise.body':
    'The bot works the other way too: when you finish a task, it sends you praise. It notices how long the task took and how often it was postponed — closing an old postponement counts for more than routine.',
  'guide.where.title':
    'Where a new task lands',
  'guide.where.body':
    'Without a named day, the task goes where Quick Add points — Today or the Backlog, per your choice in Settings. A day written in the message always wins.',
  'clients.title': 'Clients',
  'clients.client': 'Client',
  'clients.none': 'No client',
  'clients.inherited': 'from the project',
  'clients.namePlaceholder': 'Client name',
  'clients.ratePlaceholder': 'Rate per hour',
  'clients.add': 'Add',
  'clients.empty.title': 'No clients yet',
  'clients.empty.hint': 'Add a client and set it on a project task — time on its steps then counts by itself.',
  'clients.billing': 'Billing details',
  'clients.rate': 'Hourly rate',
  'clients.company': 'Company',
  'clients.registrationNo': 'Registration no.',
  'clients.address': 'Address',
  'clients.archived': 'archived',
  'clients.archive': 'Archive',
  'clients.unarchive': 'Restore',
  'clients.unbilled': 'Unbilled time',
  'clients.nothingUnbilled': 'Nothing unbilled in this period.',
  'clients.total': 'Total',
  'clients.issueReport': 'Issue a work report',
  'clients.issueHint': 'Issuing marks the entries as billed and opens the print page. The next report will not offer them again.',
  'clients.reports': 'Issued reports',
  'clients.print': 'Print',
  'time.title': 'Time',
  'time.tracked': 'Tracked',
  'time.startFor': 'Start the timer: {title}',
  'time.startParallel': 'Add a parallel timer',
  'time.startParallelFor': 'Add a parallel timer: {title}',
  'time.start': 'Start',
  'time.stop': 'Stop',
  'time.addManual': 'Add time by hand',
  'time.save': 'Save',
  'time.billed': 'billed',
  'time.running': 'running…',
  'time.edit': 'Edit entry',
  'time.delete': 'Delete entry',
  'report.title': 'Work report',
  'report.back': 'Back to the client',
  'report.print': 'Print / save PDF',
  'report.supplier': 'Supplier',
  'report.client': 'Client',
  'report.date': 'Date',
  'report.work': 'Work',
  'report.hours': 'Time',
  'report.rate': 'Rate',
  'report.amount': 'Amount',
  'report.total': 'Total',
  'report.issued': 'Issued',
  'clients.unbilledTotal': 'Unbilled in total: {amount}',
  'clients.allBilled': 'Everything is billed.',
  'report.void': 'Void the report',
  'report.voidConfirm': 'Void report {number}? Its entries return to unbilled; the number is never reused.',
  'report.markPaid': 'Mark as paid',
  'report.paid': 'paid',
  'report.unpaid': 'awaiting payment',
  'report.paidOn': 'Paid {date}',
  'billing.fixedPrice': 'Fixed price',
  'billing.fixedPriceHint': 'A price for the whole job instead of a rate per hour. Time is still tracked, but not billed.',
  'billing.fixedPricePlaceholder': 'e.g. 1500',
  'billing.perHour': 'By the hour',
  'billing.clear': 'Remove the fixed price',
  'billing.billedCannotEdit': 'This amount is on report {number}. Put it back to unbilled in the settlement list to change it.',
  'billing.state.AGREED': 'Agreed',
  'billing.state.UNBILLED': 'To invoice',
  'billing.state.OWED': 'Owed',
  'billing.state.PAID': 'Paid',
  'billing.state.NONE': 'No amount',
  'billing.hint.AGREED': 'The price is agreed, but the task is not finished — it bills when it is done.',
  'billing.hint.UNBILLED': 'Done and not invoiced yet.',
  'billing.hint.OWED': 'On a report, not paid yet.',
  'billing.hint.PAID': 'The money arrived.',
  'billing.mode.instead': 'instead of time',
  'billing.mode.with': 'on top of time',
  'billing.mode.insteadHint': 'A fixed price for the job. Time is tracked, but not billed.',
  'billing.mode.withHint': 'An extra cost — a part, a licence, delivery. The tracked hours bill as well.',
  'projects.kind.EXTRA': 'extra cost + hours',
  'billing.settledCannotEdit': 'This amount is settled. Put it back to unbilled in the settlement list to change it.',
  'billing.hint.WRITTEN_OFF': 'Written off — not charged.',
  'billing.state.WRITTEN_OFF': 'Written off',
  'projects.money': 'Money',
  'projects.owed': 'Owed',
  'projects.paid': 'Paid',
  'projects.showBilling': 'Break down by task',
  'projects.hideBilling': 'Hide the breakdown',
  'projects.noBillingRows': 'No task here has tracked time or a price yet.',
  'projects.kind.FIXED': 'fixed price',
  'projects.kind.HOURLY': 'by the hour',
  'clients.fixedCharge': 'fixed price',
  'clients.fixedCharges': 'Fixed prices',
  'ledger.state.PARTIAL': 'Part paid',
  'ledger.oneCurrency': 'A payment is in one currency — pick items of a single client.',
  'ledger.payment': 'How much arrived?',
  'ledger.paymentHint': 'Less than the picked total is spread from the oldest item; the rest stays owed.',
  'ledger.paymentConfirm': 'Record the payment',
  'ledger.paidOf': '{paid} of {total} paid',
  'ledger.paidNothing': 'Enter the amount that actually arrived.',
  'task.startProgress': 'Start',
  'task.stopProgress': 'Back to to do',
  'board.title': 'Board',
  'board.stage.OPEN': 'To do',
  'board.stage.IN_PROGRESS': 'In progress',
  'board.stage.COMPLETED': 'Done',
  'board.empty': 'Empty',
  'board.more': '{count} more',
  'board.moveTo': 'Move to {stage}',
  'board.hint': 'Drag a card between the columns. Dropping it in Done completes the task.',
  'board.showBoard': 'Board',
  'board.showList': 'List',
  'nav.board': 'Board',
  'ledger.title': 'Settlement',
  'ledger.all': 'All',
  'ledger.state.UNBILLED': 'To invoice',
  'ledger.state.INVOICED': 'Invoiced',
  'ledger.state.PAID': 'Paid',
  'ledger.state.WRITTEN_OFF': 'Written off',
  'ledger.day': 'Date',
  'ledger.work': 'Work',
  'ledger.amount': 'Amount',
  'ledger.state': 'State',
  'ledger.fixed': 'fixed amount',
  'ledger.notDue': 'not finished yet',
  'ledger.empty': 'Nothing here.',
  'ledger.selectAll': 'Select all',
  'ledger.picked': '{count} picked · {amount}',
  'ledger.issue': 'Issue a report',
  'ledger.markInvoiced': 'Mark invoiced',
  'ledger.markPaid': 'Paid…',
  'ledger.writeOff': 'Write off',
  'ledger.markUnbilled': 'Put back',
  'ledger.moved': 'Moved {count} items to {state}',
  'ledger.unbillConfirm': 'The picked items are on an issued report. Putting them back takes them off it so they can be billed again. The report itself stays exactly as issued.',
  'ledger.hint': 'Tick the items and say what really happened to them. Every move can be undone.',
  'projects.title': 'Projects',
  'projects.none': 'No project',
  'projects.one': 'Project',
  'projects.namePlaceholder': 'Project name',
  'projects.add': 'Create',
  'projects.empty.title': 'No projects yet',
  'projects.empty.hint': 'A project gathers tasks, their hours and the billing for one client — unlike a label, a task belongs to at most one, so the hours can be added up.',
  'projects.tasks': 'Tasks',
  'projects.tracked': 'Tracked',
  'projects.unbilled': 'Unbilled',
  'projects.progress': 'Done',
  'projects.late': {
    one: '{count} day late',
    other: '{count} days late',
  },
  'projects.overdue': {
    one: '{count} overdue',
    other: '{count} overdue',
  },
  'projects.showArchived': 'Archive',
  'projects.hideArchived': 'Hide archive',
  'projects.due': 'Due',
  'projects.color': 'Colour',
  'projects.edit': 'Edit the project',
  'projects.delete': 'Delete the project',
  'projects.search': 'Search a project or client…',
  'projects.noMatch': 'Nothing found',
  'projects.noMatchHint': 'Try another word, or look in the archive.',
} as const;
